import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import StatusBadge from '../account/components/StatusBadge';
import CouponFormModal from './components/CouponFormModal';
import AlertService from '../../utils/alertService';
import {
  Button, Card, Counters, EmptyState, IconButton, SegmentedControl, SkeletonRows,
} from '../../components/app2';
import billingService, { formatMxn, formatDateLong } from '../../utils/billingService';

/**
 * Cupones de descuento (rediseño jul-2026).
 *
 * Mismo alta/edición/desactivación de siempre, con el molde del sistema: el
 * filtro de estado pasa a control segmentado, la tabla se vuelve tarjetas en
 * móvil y el listado gana una barra de contadores (activos / canjes) que se
 * calcula con los cupones ya cargados, sin pedir nada nuevo al API.
 *
 * El título lo pinta el header del shell; la pantalla ya no repite su <h1>.
 */

const PAGE_SIZE = 25;

/* El filtro viaja al API como cadena ('true'/'false'/vacío); el id es solo la
   llave del control segmentado. */
const ESTADO_OPCIONES = [
  { id: 'todos', label: 'Todos', text: true, api: '' },
  { id: 'activos', label: 'Activos', text: true, api: 'true' },
  { id: 'inactivos', label: 'Inactivos', text: true, api: 'false' },
];

/* Seis columnas + acciones. 828 de columnas + 84 de separaciones + 40 de
   padding = 952; por debajo de 1024 se cambia a tarjetas. */
const GRID = 'minmax(150px,1.2fr) 110px 128px 108px minmax(170px,1fr) 104px 82px';
const GRID_GAP = 14;
const GRID_MIN = 952;

const descuentoDe = (c) => (
  c.discount_type === 'percentage' ? `${c.percent_off}%` : formatMxn(c.amount_off)
);

const duracionDe = (c) => {
  if (c.duration === 'once') return 'Una vez';
  if (c.duration === 'repeating') return `${c.duration_in_months} meses`;
  return 'Permanente';
};

const canjesDe = (c) => `${c.times_redeemed || 0}${c.max_redemptions ? ` / ${c.max_redemptions}` : ''}`;

const vigenciaDe = (c) => (
  <>
    {c.valid_from ? formatDateLong(c.valid_from) : 'Desde siempre'}
    <span className="a2-t-faint"> a </span>
    {c.valid_until ? formatDateLong(c.valid_until) : 'sin fecha de fin'}
  </>
);

const AdminCoupons = () => {
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    loadCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isActiveFilter]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const res = await billingService.admin.listCoupons({
        page,
        pageSize: PAGE_SIZE,
        isActive: isActiveFilter || undefined,
      });
      setCoupons(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta?.pagination || null);
    } catch (err) {
      console.error('Error loading coupons', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (coupon) => {
    const confirmed = await AlertService?.confirm(
      '¿Desactivar cupón?',
      `El cupón ${coupon.code} se marcará como inactivo y dejará de poder canjearse. Las suscripciones que ya lo aplicaron mantendrán el descuento.`,
      { confirmText: 'Desactivar', cancelText: 'Cancelar', confirmColor: '#dc2626' },
    );
    if (!confirmed) return;
    try {
      await billingService.admin.deleteCoupon(coupon.id);
      AlertService?.success('Cupón desactivado', '', { autoClose: true });
      loadCoupons();
    } catch (err) {
      AlertService?.error('Error', err?.response?.data?.error?.message || err?.message);
    }
  };

  const contadores = [
    { label: isActiveFilter === '' ? 'Cupones' : 'En esta vista', value: meta?.total ?? coupons.length },
    { label: 'Activos', value: coupons.filter((c) => c.is_active).length, tone: 'ok' },
    { label: 'Canjes acumulados', value: coupons.reduce((t, c) => t + (c.times_redeemed || 0), 0) },
  ];

  /** Acciones de un cupón (mismas en tabla y en tarjeta). */
  const Acciones = ({ coupon }) => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
      <IconButton
        icon="edit"
        title={`Editar ${coupon.code}`}
        aria-label={`Editar ${coupon.code}`}
        onClick={() => setEditing(coupon)}
      />
      {coupon.is_active ? (
        <IconButton
          icon="block"
          tone="danger"
          title={`Desactivar ${coupon.code}`}
          aria-label={`Desactivar ${coupon.code}`}
          onClick={() => handleDelete(coupon)}
        />
      ) : null}
    </div>
  );

  const Codigo = ({ coupon }) => (
    <div style={{ minWidth: 0 }}>
      <div className="a2-tcell-strong a2-mono a2-truncate" title={coupon.code}>{coupon.code}</div>
      {coupon.name ? (
        <div className="a2-tcell-muted a2-truncate" title={coupon.name}>{coupon.name}</div>
      ) : null}
    </div>
  );

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>Cupones - PaySlip</title>
        <meta name="description" content="Crea y administra los códigos promocionales de PaySlip" />
      </Helmet>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 16,
        }}
      >
        <Counters items={contadores} />
        <Button icon="add" onClick={() => setShowCreate(true)} style={{ marginLeft: 'auto' }}>
          Nuevo cupón
        </Button>
      </div>

      <Card pad={false}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h3 className="a2-card-title">Códigos promocionales</h3>
              <p className="a2-card-sub">Cada alta o cambio se sincroniza con Stripe</p>
            </div>
            <SegmentedControl
              items={ESTADO_OPCIONES}
              value={ESTADO_OPCIONES.find((o) => o.api === isActiveFilter)?.id || 'todos'}
              onChange={(id) => {
                setPage(1);
                setIsActiveFilter(ESTADO_OPCIONES.find((o) => o.id === id)?.api ?? '');
              }}
              role="group"
              aria-label="Filtrar cupones por estado"
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 20 }}>
            <SkeletonRows rows={5} title={false} />
          </div>
        ) : coupons.length === 0 ? (
          <EmptyState
            icon="sell"
            title={isActiveFilter === '' ? 'Todavía no hay cupones' : 'Ningún cupón con este estado'}
            text={isActiveFilter === ''
              ? 'Crea un código promocional y podrás aplicarlo al contratar o al actualizar un plan.'
              : 'Cambia el filtro para ver los demás cupones.'}
            action={isActiveFilter === ''
              ? <Button icon="add" onClick={() => setShowCreate(true)}>Crear el primero</Button>
              : null}
            className="a2-card"
            style={{ border: 'none' }}
          />
        ) : (
          <>
            {/* Escritorio */}
            <div className="a2-only-lg">
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: GRID_MIN }}>
                  <div className="a2-thead" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                    <div>Código</div>
                    <div>Descuento</div>
                    <div>Duración</div>
                    <div>Canjeado</div>
                    <div>Vigencia</div>
                    <div>Estado</div>
                    <div style={{ textAlign: 'right' }}>Acciones</div>
                  </div>

                  {coupons.map((c) => (
                    <div key={c.id} className="a2-trow" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                      <Codigo coupon={c} />
                      <div className="a2-tcell-strong">{descuentoDe(c)}</div>
                      <div className="a2-tcell">{duracionDe(c)}</div>
                      <div className="a2-tcell a2-mono">{canjesDe(c)}</div>
                      <div className="a2-tcell">{vigenciaDe(c)}</div>
                      <div>
                        <StatusBadge
                          tone={c.is_active ? 'success' : 'muted'}
                          label={c.is_active ? 'Activo' : 'Inactivo'}
                        />
                      </div>
                      <Acciones coupon={c} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Móvil */}
            <div className="a2-only-sm">
              {coupons.map((c) => (
                <div key={c.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <Codigo coupon={c} />
                    <Acciones coupon={c} />
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <StatusBadge
                      tone={c.is_active ? 'success' : 'muted'}
                      label={c.is_active ? 'Activo' : 'Inactivo'}
                    />
                  </div>

                  <div
                    style={{
                      marginTop: 10, display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10,
                    }}
                  >
                    <div>
                      <div className="a2-tcell-muted">Descuento</div>
                      <div className="a2-tcell-strong">{descuentoDe(c)}</div>
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Duración</div>
                      <div className="a2-tcell">{duracionDe(c)}</div>
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Canjeado</div>
                      <div className="a2-tcell a2-mono">{canjesDe(c)}</div>
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Vigencia</div>
                      <div className="a2-tcell">{vigenciaDe(c)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {meta && meta.pageCount > 1 ? (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--line)',
            }}
          >
            <Button
              variant="outline"
              size="sm"
              icon="chevron_left"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="a2-tcell-muted">
              Página {meta.page} de {meta.pageCount} · {meta.total} cupones
            </span>
            <Button
              variant="outline"
              size="sm"
              iconRight="chevron_right"
              disabled={page >= meta.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        ) : null}
      </Card>

      {(editing || showCreate) && (
        <CouponFormModal
          existingCoupon={editing}
          onClose={() => {
            setEditing(null);
            setShowCreate(false);
          }}
          onSaved={() => {
            setEditing(null);
            setShowCreate(false);
            loadCoupons();
          }}
        />
      )}
    </div>
  );
};

export default AdminCoupons;
